import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import "./ListProduct.css";
import { isAdmin } from "../../utils/roleUtils";
import editIcon from "../Assets/edit.png";
import { backend_url, currency } from "../../App";
import { ToastContainer, toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProductPopup from "../ProductDetailsPopup/ProductPopup";
import BarcodeModal from "../BarcodeModal/BarcodeModal";
import ActionPopup from "../ActionPopup/ActionPopup";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { menuItems } from "../../Constants/menuItems";

/* ─── Excel helpers ─────────────────────────── */
const COL_STOCK_NO    = "stock no";
const COL_RETAIL      = "retail price";
const COL_CLOSING_QTY = "closing bal.qty";
const KNOWN_CATEGORY_VALUES = new Set(menuItems.map((m) => m.value?.toLowerCase()));
const isKnownCategory = (cat) => !cat || KNOWN_CATEGORY_VALUES.has(cat?.toLowerCase());
const getCatLabel     = (cat) => menuItems.find((m) => m.value?.toLowerCase() === cat?.toLowerCase())?.label || null;

const CATEGORY_TABS = [
  { label: "All", value: "all" },
  ...menuItems.map((m) => ({ label: m.label, value: m.value })),
  { label: "Unknown", value: "__unknown__" },
];

const SUBCAT_COLORS = [
  "#7c3aed","#0891b2","#059669","#d97706","#dc2626",
  "#db2777","#2563eb","#65a30d","#9333ea","#0d9488",
  "#b45309","#e11d48","#0369a1","#16a34a","#7e22ce",
  "#c2410c","#1d4ed8","#047857","#b91c1c","#6d28d9",
];

const normalizeKey  = (k) => k.trim().toLowerCase();
// Same rule as the Stock page: sarees 0 out / 1–2 low; fabric under 1m out / 1–14 low
const getAutoStatus = (qty, category) => {
  const n = Number(qty);
  if (isNaN(n) || n <= 0) return "Out of Stock";
  if (category?.toLowerCase() === "sarees") return n < 3 ? "Low Stock" : "In-Stock";
  if (n < 1)             return "Out of Stock";
  if (n < 15)            return "Low Stock";
  return "In-Stock";
};
const isDiff = (a, b) => {
  const na = Number(a), nb = Number(b);
  if (!isNaN(na) && !isNaN(nb) && a !== "" && b !== "") return na !== nb;
  return String(a).trim() !== String(b).trim();
};
const fmtPrice  = (v)    =>
  v !== "" && v !== undefined && v !== null
    ? `₹${Number(v).toLocaleString("en-IN")}`
    : "—";
/* ─────────────────────────────────────────────── */

const ListProduct = () => {
  /* ── page tab ── */
  const [activeTab, setActiveTab] = useState("products"); // 'products' | 'archived' | 'excel' | 'discount'

  /* ── products tab state ── */
  const [allProducts,        setAllProducts]        = useState([]);
  const [paginationInfo,     setPaginationInfo]     = useState({ totalPages: 1, currentPage: 1 });
  const [searchTerm,         setSearchTerm]         = useState("");
  // Archive / restore pop-up: { product, archived }
  const [archivePopup,       setArchivePopup]       = useState(null);
  const [prodActiveCategory,    setProdActiveCategory]    = useState("all");
  const [prodActiveSubcategory, setProdActiveSubcategory] = useState("all");
  const [stockFilter,           setStockFilter]           = useState("");
  const productsPerPage = 10;
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct,  setSelectedProduct]  = useState(null);
  const [barcodeProduct,   setBarcodeProduct]   = useState(null);
  const [isLoading,        setIsLoading]        = useState(false);

  /* ── excel tab state ── */
  const [exRows,           setExRows]           = useState([]);
  const [exError,          setExError]          = useState("");
  const [exFileName,       setExFileName]       = useState("");
  const [exDragging,       setExDragging]       = useState(false);
  const [exSearch,         setExSearch]         = useState("");
  const [exSaving,         setExSaving]         = useState(false);
  const [exSaveStatus,     setExSaveStatus]     = useState({});
  const [exShowChanged,    setExShowChanged]    = useState(false);
  const [exStatusFilter,   setExStatusFilter]   = useState("all"); // all | matched | changed | nochange | notfound
  const [exActiveCategory, setExActiveCategory] = useState("all");
  const exFileRef = useRef();

  /* ── discount tab state ── */
  const [discountInputs,    setDiscountInputs]    = useState({});
  const [discountSaving,    setDiscountSaving]    = useState({});
  const [discountResult,    setDiscountResult]    = useState({});
  const [globalDiscount,    setGlobalDiscount]    = useState("");
  const [globalSaving,      setGlobalSaving]      = useState(false);
  const [confirmDiscount,   setConfirmDiscount]   = useState(null);
  const [drillCategory,     setDrillCategory]     = useState(null); // menuItem object when drilling into subcats

  /* ════════════════════════════════════════════
     PRODUCTS TAB — logic
  ════════════════════════════════════════════ */
  useEffect(() => {
    setPaginationInfo((p) => ({ ...p, totalPages: Math.ceil(filteredProducts.length / productsPerPage) }));
  }, [filteredProducts]);

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${backend_url}/allproducts?includeArchived=true`);
        const data = await res.json();
        setAllProducts(data);
      } catch { toast.error("Failed to load products"); }
    })();
  }, []);

  /* active vs archived split */
  const activeProducts   = allProducts.filter((p) => !p.isArchived);
  const archivedProducts = allProducts.filter((p) => p.isArchived);

  /* centralised filter — always derive from active (non-archived) products */
  const applyFilters = ({
    search   = searchTerm,
    category = prodActiveCategory,
    subcat   = prodActiveSubcategory,
    stock    = stockFilter,
  }) => {
    let list = allProducts.filter((p) => !p.isArchived);
    if (category !== "all" && category !== "__unknown__")
      list = list.filter((p) => p.category?.toLowerCase() === category.toLowerCase());
    if (category === "__unknown__")
      list = list.filter((p) => !isKnownCategory(p.category));
    if (subcat !== "all")
      list = list.filter((p) => p.subcategory?.toLowerCase() === subcat.toLowerCase());
    if (stock)
      list = list.filter((p) => p.status === stock);
    if (search.trim())
      list = list.filter((p) =>
        p.name.toLowerCase().includes(search) || p.unique_id.toLowerCase().includes(search)
      );
    setFilteredProducts(list);
    setPaginationInfo((p) => ({ ...p, currentPage: 1 }));
  };

  /* re-derive filtered list whenever products change (fetch, archive, restore, bulk updates) */
  useEffect(() => {
    applyFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts]);

  const handleSearch = (e) => {
    const v = e.target.value.toLowerCase();
    setSearchTerm(v);
    applyFilters({ search: v });
  };

  const handleStockFilter = (v) => {
    setStockFilter(v);
    applyFilters({ stock: v });
  };

  const handleProdCategoryChange = (val) => {
    setProdActiveCategory(val);
    setProdActiveSubcategory("all");
    setSearchTerm("");
    applyFilters({ category: val, subcat: "all", search: "" });
  };

  const handleProdSubcategoryChange = (val) => {
    setProdActiveSubcategory(val);
    applyFilters({ subcat: val });
  };

  /* subcategory list for active category */
  const activeMenuCat  = menuItems.find((m) => m.value === prodActiveCategory);
  const subcategoryList = activeMenuCat
    ? activeMenuCat.dropdown.flatMap((d) =>
        d.subcategories ? d.subcategories : [d]
      ).filter((s) => s.value)
    : [];

  // Returns { ok } or { ok: false, message } — the archive pop-up shows the result
  const archiveProduct = async (id, archived = true) => {
    try {
      const response = await fetch(`${backend_url}/archiveproduct`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ id, archived }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setAllProducts((prev) =>
          prev.map((p) => (p.id === id ? { ...p, isArchived: archived } : p))
        );
        return { ok: true };
      }
      return { ok: false, message: data.message || (archived ? "Failed to archive the product." : "Failed to restore the product.") };
    } catch (error) {
      return { ok: false, message: "Could not reach the server — check your connection and try again." };
    }
  };

  const handlePageChange = (n) => setPaginationInfo((p) => ({ ...p, currentPage: n }));


  const startIndex        = (paginationInfo.currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);
  const totalPages        = Math.ceil(filteredProducts.length / productsPerPage);

  /* ════════════════════════════════════════════
     EXCEL TAB — logic
  ════════════════════════════════════════════ */
  const enrichWithDb = async (parsed) => {
    try {
      const res  = await fetch(`${backend_url}/allproducts`);
      const data = await res.json();
      const byId = {};
      data.forEach((p) => {
        const key = p.unique_id?.trim().toLowerCase();
        if (!byId[key]) byId[key] = [];
        byId[key].push(p);
      });
      return parsed.map((row) => {
        const key     = String(row.stockNo).trim().toLowerCase();
        const matches = byId[key] || [];
        const primary = matches[0] || null;
        return {
          ...row,
          dbProduct:    primary,
          dbMatches:    matches,
          priceChanged: primary ? isDiff(row.retailPrice, primary.old_price) : false,
          qtyChanged:   primary ? isDiff(row.closingQty,  primary.quantity)  : false,
        };
      });
    } catch {
      toast.error("Could not fetch products from server.");
      return parsed.map((row) => ({ ...row, dbProduct: null, dbMatches: [], priceChanged: false, qtyChanged: false }));
    }
  };

  const parseExcelFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setExError("Please upload a valid Excel (.xlsx, .xls) or CSV file.");
      return;
    }
    setExError(""); setExFileName(file.name); setExRows([]); setExSaveStatus({}); setExActiveCategory("all");

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data     = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet    = workbook.Sheets[workbook.SheetNames[0]];
      const json     = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!json.length) { setExError("The file is empty."); return; }

      const rawKeys = Object.keys(json[0]);
      const keyMap  = {};
      rawKeys.forEach((rk) => { keyMap[normalizeKey(rk)] = rk; });

      const required = [COL_STOCK_NO, COL_RETAIL, COL_CLOSING_QTY];
      const missing  = required.filter((k) => !keyMap[k]);
      if (missing.length) {
        setExError(`Missing columns: "${missing.join('", "')}". Found: ${rawKeys.join(", ")}`);
        return;
      }

      const parsed = json.map((row, idx) => ({
        _idx: idx,
        stockNo:     row[keyMap[COL_STOCK_NO]],
        retailPrice: row[keyMap[COL_RETAIL]],
        closingQty:  row[keyMap[COL_CLOSING_QTY]],
        dbProduct: null, priceChanged: false, qtyChanged: false,
      }));

      setExRows(await enrichWithDb(parsed));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExFileDrop = (e) => { e.preventDefault(); setExDragging(false); parseExcelFile(e.dataTransfer.files[0]); };
  const handleExFileClear = () => {
    setExRows([]); setExFileName(""); setExError("");
    setExSearch(""); setExSaveStatus({}); setExActiveCategory("all"); setExStatusFilter("all");
    if (exFileRef.current) exFileRef.current.value = "";
  };

  const buildExPayload = (row) => {
    const payload = {};
    if (row.qtyChanged)   { payload.quantity = row.closingQty; payload.status = getAutoStatus(row.closingQty, row.dbProduct?.category); }
    if (row.priceChanged) { payload.old_price = row.retailPrice; }
    return payload;
  };

  const patchExAllMatches = async (row) => {
    const targets = row.dbMatches?.length ? row.dbMatches : [row.dbProduct];
    const payload = buildExPayload(row);
    const results = await Promise.all(
      targets.map((p) =>
        fetch(`${backend_url}/updatestock/${p._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).then((r) => r.json()).catch(() => ({ success: false }))
      )
    );
    return results.every((r) => r.success) ? "ok" : "err";
  };

  const handleExSaveRow = async (row) => {
    setExSaveStatus((s) => ({ ...s, [row._idx]: "saving" }));
    const status = await patchExAllMatches(row);
    setExSaveStatus((s) => ({ ...s, [row._idx]: status }));
    const count = row.dbMatches?.length || 1;
    if (status === "ok") toast.success(`${row.stockNo} — ${count} product(s) updated.`);
    else                 toast.error(`Failed to update ${row.stockNo}.`);
  };

  const handleExUpdateAll = async () => {
    const toUpdate = exDisplayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
    if (!toUpdate.length) { toast.info("No differences found — nothing to update."); return; }
    setExSaving(true);
    const newStatus = { ...exSaveStatus };
    await Promise.all(toUpdate.map(async (row) => {
      newStatus[row._idx] = await patchExAllMatches(row);
    }));
    setExSaveStatus(newStatus);
    setExSaving(false);
    const ok  = Object.values(newStatus).filter((v) => v === "ok").length;
    const err = Object.values(newStatus).filter((v) => v === "err").length;
    if (ok) {
      toast.success(`${ok} product(s) updated successfully.`);
      const fresh = await fetch(`${backend_url}/allproducts`).then((r) => r.json());
      setAllProducts(fresh);
      setFilteredProducts(fresh);
    }
    if (err) toast.error(`${err} product(s) failed to update.`);
  };

  /* excel filter pipeline */
  let exDisplayed = exRows;
  if (exActiveCategory === "__unknown__")
    exDisplayed = exDisplayed.filter((r) => r.dbProduct && !isKnownCategory(r.dbProduct.category));
  else if (exActiveCategory !== "all")
    exDisplayed = exDisplayed.filter((r) => r.dbProduct?.category?.toLowerCase() === exActiveCategory.toLowerCase());
  if (exSearch.trim()) {
    const q = exSearch.toLowerCase();
    exDisplayed = exDisplayed.filter((r) => String(r.stockNo).toLowerCase().includes(q));
  }
  if (exShowChanged)
    exDisplayed = exDisplayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
  if (exStatusFilter === "matched")  exDisplayed = exDisplayed.filter((r) => r.dbProduct);
  if (exStatusFilter === "changed")  exDisplayed = exDisplayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged));
  if (exStatusFilter === "nochange") exDisplayed = exDisplayed.filter((r) => r.dbProduct && !r.priceChanged && !r.qtyChanged);
  if (exStatusFilter === "notfound") exDisplayed = exDisplayed.filter((r) => !r.dbProduct);

  // sort: Pending first → No Change → Not Found
  const exSortRank = (r) => {
    if (r.dbProduct && (r.priceChanged || r.qtyChanged)) return 0;
    if (r.dbProduct && !r.priceChanged && !r.qtyChanged) return 1;
    return 2;
  };
  exDisplayed = [...exDisplayed].sort((a, b) => exSortRank(a) - exSortRank(b));

  const exScoped =
    exActiveCategory === "all"         ? exRows :
    exActiveCategory === "__unknown__" ? exRows.filter((r) => r.dbProduct && !isKnownCategory(r.dbProduct.category)) :
    exRows.filter((r) => r.dbProduct?.category?.toLowerCase() === exActiveCategory.toLowerCase());

  const exMatchedCnt  = exScoped.filter((r) => r.dbProduct).length;
  const exChangedCnt  = exScoped.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged)).length;
  const exNoChgCnt    = exScoped.filter((r) => r.dbProduct && !r.priceChanged && !r.qtyChanged).length;
  const exNotFoundCnt = exScoped.filter((r) => !r.dbProduct).length;
  const exCatCount    = (val) => {
    if (val === "all")         return exRows.length;
    if (val === "__unknown__") return exRows.filter((r) => r.dbProduct && !isKnownCategory(r.dbProduct.category)).length;
    return exRows.filter((r) => r.dbProduct?.category?.toLowerCase() === val.toLowerCase()).length;
  };
  const exPendingCnt  = exDisplayed.filter((r) => r.dbProduct && (r.priceChanged || r.qtyChanged)).length;

  /* ════════════════════════════════════════════
     DISCOUNT TAB — logic
  ════════════════════════════════════════════ */
  const applyDiscount = async (filterFn, label, pct, key) => {
    const targets = filterFn(activeProducts);
    if (!targets.length) return;

    setDiscountSaving((s) => ({ ...s, [key]: true }));
    setDiscountResult((r) => ({ ...r, [key]: null }));

    let ok = 0, err = 0;
    await Promise.all(targets.map(async (p) => {
      try {
        const res = await fetch(`${backend_url}/updatestock/${p._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ new_price: Number(pct) }),
        });
        const data = await res.json();
        data.success ? ok++ : err++;
      } catch { err++; }
    }));

    setDiscountSaving((s) => ({ ...s, [key]: false }));
    setDiscountResult((r) => ({ ...r, [key]: { ok, err } }));
    setConfirmDiscount(null);
    if (ok) {
      toast.success(`${ok} product(s) in "${label}" → ${pct}% discount applied.`);
      // refresh product list so new_price reflects immediately
      const fresh = await fetch(`${backend_url}/allproducts`).then((r) => r.json());
      setAllProducts(fresh);
      setFilteredProducts(fresh);
    }
    if (err) toast.error(`${err} product(s) failed.`);
  };

  /* build card data for top-level categories */
  const buildCardData = (items, filterFn) =>
    items.map((m) => {
      const prods     = filterFn(m, activeProducts);
      const discounts = prods.map((p) => Number(p.new_price)).filter((n) => !isNaN(n));
      const avg = discounts.length ? Math.round(discounts.reduce((a, b) => a + b, 0) / discounts.length) : 0;
      const min = discounts.length ? Math.min(...discounts) : 0;
      const max = discounts.length ? Math.max(...discounts) : 0;
      return { ...m, count: prods.length, avgDisc: avg, minDisc: min, maxDisc: max };
    }).filter((m) => m.count > 0);

  const discountCategoryData = buildCardData(
    menuItems,
    (m, prods) => prods.filter((p) => p.category?.toLowerCase() === m.value?.toLowerCase())
  );

  /* subcategory cards for the drilled-in category */
  const getAllSubcats = (menuItem) => {
    if (!menuItem) return [];
    return menuItem.dropdown.flatMap((d) =>
      d.subcategories
        ? d.subcategories
        : [d]
    ).filter((s) => s.value);
  };

  const discountSubcategoryData = drillCategory
    ? buildCardData(
        getAllSubcats(drillCategory),
        (s, prods) => prods.filter(
          (p) => p.category?.toLowerCase() === drillCategory.value?.toLowerCase()
            && p.subcategory?.toLowerCase() === s.value?.toLowerCase()
        )
      )
    : [];

  /* ═══════════════════════════════════════════ RENDER ═══════════════════════════════════════════ */
  return (
    <>
      <div className="product-list-container">

        {/* ── Page Tabs ── */}
        <div className="lp-tabs-bar">
          <button className={`lp-tab-btn ${activeTab === "products" ? "active" : ""}`} onClick={() => setActiveTab("products")}>
            &#128230; All Products
          </button>
          <button className={`lp-tab-btn ${activeTab === "archived" ? "active" : ""}`} onClick={() => setActiveTab("archived")}>
            &#128451; Archived
            {archivedProducts.length > 0 && <span className="lp-archived-count">{archivedProducts.length}</span>}
          </button>
          <button className={`lp-tab-btn ${activeTab === "excel" ? "active" : ""}`} onClick={() => setActiveTab("excel")}>
            &#128196; Excel Update
          </button>
          <button className={`lp-tab-btn lp-tab-discount ${activeTab === "discount" ? "active" : ""}`} onClick={() => setActiveTab("discount")}>
            &#127991; Bulk Discount
          </button>
        </div>

        {/* ══════════════════════════════════════
            TAB: PRODUCTS
        ══════════════════════════════════════ */}
        {activeTab === "products" && (
          <>
            {/* ── Header ── */}
            <div className="lp-header-row">
              <div className="lp-header-left">
                <h1 className="product-list-title">
                  {prodActiveCategory === "all" ? "All Products"
                    : prodActiveCategory === "__unknown__" ? "Unknown Category"
                    : activeMenuCat?.label ?? prodActiveCategory}
                  <span className="lp-prod-count">{filteredProducts.length}</span>
                </h1>
                {prodActiveSubcategory !== "all" && (
                  <span className="lp-subcat-crumb">
                    {subcategoryList.find((s) => s.value === prodActiveSubcategory)?.label}
                  </span>
                )}
              </div>
              <div className="lp-header-right">
                <select className="product-list-stock-btn" value={stockFilter}
                  onChange={(e) => handleStockFilter(e.target.value)}>
                  <option value="">All Status</option>
                  <option value="In-Stock">In-Stock</option>
                  <option value="Low Stock">Low Stock</option>
                  <option value="Out of Stock">Out of Stock</option>
                </select>
                <div className="lp-search-box">
                  <span className="lp-search-icon">&#128269;</span>
                  <input type="text" placeholder="Search products..." value={searchTerm}
                    onChange={handleSearch} className="product-list-search" />
                </div>
                <Link to="/addproduct">
                  <button className="product-list-add-btn">+ Add Product</button>
                </Link>
              </div>
            </div>

            {/* ── Category Tabs ── */}
            <div className="lp-cat-tabs-wrap">
              <div className="lp-prod-cat-tabs">
                {CATEGORY_TABS.map((tab) => {
                  const count =
                    tab.value === "all"         ? activeProducts.length :
                    tab.value === "__unknown__"  ? activeProducts.filter((p) => !isKnownCategory(p.category)).length :
                    activeProducts.filter((p) => p.category?.toLowerCase() === tab.value.toLowerCase()).length;
                  return (
                    <button key={tab.value}
                      className={`lp-prod-cat-tab ${prodActiveCategory === tab.value ? "active" : ""} ${tab.value === "__unknown__" ? "lp-tab-unknown" : ""}`}
                      onClick={() => handleProdCategoryChange(tab.value)}>
                      {tab.label}
                      <span className="lp-prod-cat-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Subcategory Dropdown Filter ── */}
            {subcategoryList.length > 0 && (() => {
              const activeSub = subcategoryList.find((s) => s.value === prodActiveSubcategory);
              const activeDot = activeSub
                ? SUBCAT_COLORS[subcategoryList.indexOf(activeSub) % SUBCAT_COLORS.length]
                : null;
              return (
                <div className="lp-subcat-filter-wrap">
                  <div className="lp-subcat-dropdown" tabIndex={0}
                    onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.classList.remove("open"); }}
                    onClick={(e) => e.currentTarget.classList.toggle("open")}>
                    <div className="lp-subcat-trigger">
                      {activeSub ? (
                        <>
                          <span className="lp-subcat-dot" style={{ background: activeDot }} />
                          <span className="lp-subcat-trigger-label">{activeSub.label}</span>
                        </>
                      ) : (
                        <span className="lp-subcat-trigger-label lp-subcat-trigger-all">All Subcategories</span>
                      )}
                      <span className="lp-subcat-arrow">▾</span>
                    </div>
                    <div className="lp-subcat-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`lp-subcat-option ${prodActiveSubcategory === "all" ? "selected" : ""}`}
                        onClick={() => { handleProdSubcategoryChange("all"); document.activeElement.blur(); }}>
                        <span className="lp-subcat-option-label">All Subcategories</span>
                        <span className="lp-subcat-option-count">
                          {activeProducts.filter((p) => p.category?.toLowerCase() === prodActiveCategory.toLowerCase()).length}
                        </span>
                      </button>
                      {subcategoryList.map((sub, idx) => {
                        const cnt = activeProducts.filter(
                          (p) => p.category?.toLowerCase()    === prodActiveCategory.toLowerCase()
                              && p.subcategory?.toLowerCase() === sub.value.toLowerCase()
                        ).length;
                        if (cnt === 0) return null;
                        const dot = SUBCAT_COLORS[idx % SUBCAT_COLORS.length];
                        return (
                          <button key={sub.value}
                            className={`lp-subcat-option ${prodActiveSubcategory === sub.value ? "selected" : ""}`}
                            onClick={() => { handleProdSubcategoryChange(sub.value); document.activeElement.blur(); }}>
                            <span className="lp-subcat-dot" style={{ background: dot }} />
                            <span className="lp-subcat-option-label">{sub.label}</span>
                            <span className="lp-subcat-option-count">{cnt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {prodActiveSubcategory !== "all" && (
                    <button className="lp-subcat-clear" onClick={() => handleProdSubcategoryChange("all")}>✕ Clear</button>
                  )}
                </div>
              );
            })()}


            {isLoading && <div className="loading-overlay"><div className="loading-spinner"></div></div>}

            {/* Table */}
            <div className="product-table-wrapper">
              <table className="product-table">
                <thead className="product-table-thead">
                  <tr>
                    <th>#</th><th>Image</th><th>Product ID</th><th>Title</th>
                    <th>Original Price</th><th>Discount (%)</th><th>Offer Price</th>
                    <th>Category</th><th>Subcategory</th><th>Barcode</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.length > 0 ? paginatedProducts.map((product, index) => (
                    <tr key={product._id}>
                      <td className="product-table-serial">{startIndex + index + 1}</td>
                      <td>
                        <LazyLoadImage src={product.images[0]} alt={product.name}
                          effect="blur" className="product-table-image" />
                      </td>
                      <td className="product-table-name">{product.unique_id}</td>
                      <td className="product-table-name">{product.name}</td>
                      <td className="product-table-price">{currency}{product.old_price}</td>
                      <td className="product-table-discount">{product.new_price}%</td>
                      <td className="product-table-offer-price">
                        {currency}{(product.old_price - (product.old_price * product.new_price) / 100).toFixed(2)}
                      </td>
                      <td className="product-table-category">{product.category}</td>
                      <td className="product-table-subcategory">
                        {product.subcategory
                          ? <span className="lp-subcat-pill">{product.subcategory}</span>
                          : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td>
                        <button className="barcode-view-btn" title="View / Print Barcode"
                          onClick={() => setBarcodeProduct(product)}>
                          <span className="barcode-glyph" aria-hidden="true" />
                          Barcode
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-details-btn" onClick={() => setSelectedProduct(product)}>View</button>
                          <Link to={`/editproduct/${product._id}`}>
                            <img src={editIcon} alt="Edit" className="action-icon" title="Edit Product" />
                          </Link>
                          {isAdmin() && (
                            <button onClick={() => setArchivePopup({ product, archived: true })}
                              className="archive-btn" title="Archive Product">
                              &#128451;
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="11" style={{ textAlign: "center", padding: "20px" }}>
                        {isLoading ? "Loading..." : "No products found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="product-list-pagination">
                <button onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
                  disabled={paginationInfo.currentPage === 1} className="pagination-btn">&laquo;</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || (n >= paginationInfo.currentPage - 2 && n <= paginationInfo.currentPage + 2))
                  .map((n) => (
                    <button key={n} onClick={() => handlePageChange(n)}
                      className={`pagination-btn ${paginationInfo.currentPage === n ? "active" : ""}`}>{n}</button>
                  ))}
                <button onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
                  disabled={paginationInfo.currentPage === totalPages} className="pagination-btn">&raquo;</button>
              </div>
            )}
          </>
        )}

        {/* ══════════════════════════════════════
            TAB: ARCHIVED PRODUCTS
        ══════════════════════════════════════ */}
        {activeTab === "archived" && (
          <>
            <div className="lp-header-row">
              <div className="lp-header-left">
                <h1 className="product-list-title">
                  Archived Products
                  <span className="lp-prod-count">{archivedProducts.length}</span>
                </h1>
              </div>
            </div>

            <div className="product-table-wrapper">
              <table className="product-table">
                <thead className="product-table-thead">
                  <tr>
                    <th>#</th><th>Image</th><th>Product ID</th><th>Title</th>
                    <th>Original Price</th><th>Discount (%)</th>
                    <th>Category</th><th>Subcategory</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedProducts.length > 0 ? archivedProducts.map((product, index) => (
                    <tr key={product._id} className="lp-archived-row">
                      <td className="product-table-serial">{index + 1}</td>
                      <td>
                        <LazyLoadImage src={product.images[0]} alt={product.name}
                          effect="blur" className="product-table-image" />
                      </td>
                      <td className="product-table-name">{product.unique_id}</td>
                      <td className="product-table-name">{product.name}</td>
                      <td className="product-table-price">{currency}{product.old_price}</td>
                      <td className="product-table-discount">{product.new_price}%</td>
                      <td className="product-table-category">{product.category}</td>
                      <td className="product-table-subcategory">
                        {product.subcategory
                          ? <span className="lp-subcat-pill">{product.subcategory}</span>
                          : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-details-btn" onClick={() => setSelectedProduct(product)}>View</button>
                          {isAdmin() && (
                            <button className="unarchive-btn" title="Restore Product"
                              onClick={() => setArchivePopup({ product, archived: false })}>
                              ↩ Restore
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                        No archived products
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════
            TAB: EXCEL UPDATE
        ══════════════════════════════════════ */}
        {activeTab === "excel" && (
          <div className="lp-excel-panel">
            <div className="lp-excel-header">
              <h2 className="lp-excel-title">Excel Update</h2>
              {exRows.length > 0 && <span className="lp-excel-badge">{exRows.length} rows loaded</span>}
            </div>

            {/* Drop Zone */}
            <div
              className={`eu-dropzone ${exDragging ? "dragging" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setExDragging(true); }}
              onDragLeave={() => setExDragging(false)}
              onDrop={handleExFileDrop}
              onClick={() => exFileRef.current?.click()}
            >
              <input ref={exFileRef} type="file" accept=".xlsx,.xls,.csv"
                style={{ display: "none" }} onChange={(e) => parseExcelFile(e.target.files[0])} />
              <div className="eu-dropzone-icon">&#128196;</div>
              <p className="eu-dropzone-text">
                {exFileName ? exFileName : "Click or drag & drop an Excel / CSV file here"}
              </p>
              <p className="eu-dropzone-hint">Supported: .xlsx, .xls, .csv</p>
              <p className="eu-dropzone-hint" style={{ marginTop: 4, color: "#574DC0" }}>
                Required columns: <strong>Stock No</strong>, <strong>Retail Price</strong>, <strong>Closing Bal.Qty</strong>
              </p>
            </div>

            {exError && <div className="eu-error">{exError}</div>}

            {exRows.length > 0 && (
              <>
                {/* Category Tabs */}
                <div className="eu-cat-tabs-wrap">
                  <div className="eu-cat-tabs lp-cat-tabs">
                    {CATEGORY_TABS.map((tab) => (
                      <button key={tab.value}
                        className={`eu-cat-tab lp-cat-tab ${exActiveCategory === tab.value ? "active" : ""}`}
                        onClick={() => { setExActiveCategory(tab.value); setExSearch(""); }}
                      >
                        {tab.label}
                        <span className="eu-cat-tab-count">{exCatCount(tab.value)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary chips */}
                <div className="eu-summary-row">
                  <button className={`eu-chip chip-total ${exStatusFilter === "all" ? "chip-active" : ""}`}
                    onClick={() => setExStatusFilter("all")}>{exScoped.length} Total</button>
                  <button className={`eu-chip chip-match ${exStatusFilter === "matched" ? "chip-active" : ""}`}
                    onClick={() => setExStatusFilter("matched")}>{exMatchedCnt} Matched</button>
                  <button className={`eu-chip chip-changed ${exStatusFilter === "changed" ? "chip-active" : ""}`}
                    onClick={() => setExStatusFilter("changed")}>{exChangedCnt} Have Changes</button>
                  <button className={`eu-chip chip-nochange ${exStatusFilter === "nochange" ? "chip-active" : ""}`}
                    onClick={() => setExStatusFilter("nochange")}>{exNoChgCnt} No Change</button>
                  <button className={`eu-chip chip-nomatch ${exStatusFilter === "notfound" ? "chip-active" : ""}`}
                    onClick={() => setExStatusFilter("notfound")}>{exNotFoundCnt} Not Found</button>
                </div>

                {/* Toolbar */}
                <div className="eu-toolbar">
                  <div className="eu-toolbar-left">
                    <div className="eu-search-wrap lp-search-wrap">
                      <span className="eu-search-icon">&#128269;</span>
                      <input type="text" placeholder="Search by Stock No..."
                        value={exSearch} onChange={(e) => setExSearch(e.target.value)} />
                    </div>
                    <label className="eu-toggle-label">
                      <input type="checkbox" checked={exShowChanged}
                        onChange={(e) => setExShowChanged(e.target.checked)} />
                      Show changed only
                    </label>
                  </div>
                  <div className="eu-toolbar-actions">
                    <button className="eu-clear-btn" onClick={handleExFileClear}>Clear &times;</button>
                    <button className="lp-excel-save-btn" onClick={handleExUpdateAll}
                      disabled={exSaving || exPendingCnt === 0}>
                      {exSaving ? "Updating..." : `Update${exActiveCategory !== "all" ? " Category" : " All"} (${exPendingCnt} changed)`}
                    </button>
                  </div>
                </div>

                {/* Comparison Table */}
                <div className="eu-table-wrapper lp-excel-table">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Image</th>
                        <th>Stock No</th>
                        <th>Category</th>
                        <th>Current Price</th>
                        <th>New Price</th>
                        <th>Current Qty</th>
                        <th>New Qty</th>
                        <th>Changes</th>
                        <th>Save</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exDisplayed.length > 0 ? exDisplayed.map((row) => {
                        const saved     = exSaveStatus[row._idx];
                        const hasChange = row.priceChanged || row.qtyChanged;
                        const dbCat     = row.dbProduct?.category;
                        const knownCat  = isKnownCategory(dbCat);

                        return (
                          <tr key={row._idx} className={
                            saved === "ok"    ? "eu-row-saved"
                            : !row.dbProduct  ? "eu-row-notfound"
                            : hasChange       ? "eu-row-changed"
                            : ""
                          }>
                            <td><div className="eu-serial">{row._idx + 1}</div></td>
                            <td>
                              {row.dbProduct?.images?.[0] ? (
                                <img
                                  src={row.dbProduct.images[0].startsWith("http")
                                    ? row.dbProduct.images[0]
                                    : `${backend_url}${row.dbProduct.images[0]}`}
                                  alt={row.dbProduct.name}
                                  className="eu-row-img"
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ) : (
                                <div className="eu-row-img-empty">—</div>
                              )}
                            </td>
                            <td className="eu-id-cell">
                              {row.stockNo}
                              {row.dbMatches?.length > 1 && (
                                <span className="eu-multi-badge" title={`${row.dbMatches.length} products share this ID — all will be updated`}>
                                  ×{row.dbMatches.length}
                                </span>
                              )}
                            </td>
                            <td>
                              {row.dbProduct ? (
                                knownCat
                                  ? <span className="eu-cat-pill">{getCatLabel(dbCat)}</span>
                                  : <span className="eu-cat-pill eu-cat-pill-unknown" title={`Raw DB value: "${dbCat}"`}>⚠ {dbCat}</span>
                              ) : (
                                <span className="eu-na">—</span>
                              )}
                            </td>
                            <td className="eu-db-val">
                              {row.dbProduct ? fmtPrice(row.dbProduct.old_price) : <span className="eu-na">—</span>}
                            </td>
                            <td className={`eu-new-val ${row.priceChanged ? "eu-cell-diff" : ""}`}>
                              {fmtPrice(row.retailPrice)}
                              {row.priceChanged && <span className="eu-diff-dot" />}
                            </td>
                            <td className="eu-db-val">
                              {row.dbProduct ? (row.dbProduct.quantity ?? "—") : <span className="eu-na">—</span>}
                            </td>
                            <td className={`eu-new-val ${row.qtyChanged ? "eu-cell-diff" : ""}`}>
                              {row.closingQty !== "" ? row.closingQty : "—"}
                              {row.qtyChanged && <span className="eu-diff-dot" />}
                            </td>
                            <td>
                              {!row.dbProduct && <span className="eu-match-badge notfound">Not Found</span>}
                              {row.dbProduct && !hasChange && <span className="eu-match-badge nochange">No Change</span>}
                              {row.dbProduct && row.priceChanged && row.qtyChanged  && <span className="eu-match-badge both">Price + Qty</span>}
                              {row.dbProduct && row.priceChanged && !row.qtyChanged && <span className="eu-match-badge price">Price</span>}
                              {row.dbProduct && !row.priceChanged && row.qtyChanged && <span className="eu-match-badge qty">Qty</span>}
                            </td>
                            <td>
                              {saved === "ok"  && <span className="eu-save-badge ok">✓ Updated</span>}
                              {saved === "err" && (
                                <button className="eu-row-save-btn eu-row-save-retry"
                                  onClick={() => handleExSaveRow(row)}>↺ Retry</button>
                              )}
                              {saved === "saving" && <span className="eu-save-badge pending">Saving…</span>}
                              {!saved && row.dbProduct && hasChange && (
                                <button className="eu-row-save-btn"
                                  onClick={() => handleExSaveRow(row)}>Save</button>
                              )}
                              {!saved && (!row.dbProduct || !hasChange) && <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>}
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan="10">
                            <div className="eu-empty">
                              No records found{exActiveCategory !== "all"
                                ? ` in ${CATEGORY_TABS.find((t) => t.value === exActiveCategory)?.label}`
                                : ""}.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
        {/* ══════════════════════════════════════
            TAB: BULK DISCOUNT
        ══════════════════════════════════════ */}
        {activeTab === "discount" && (
          <div className="bd-panel">

            {/* Header + breadcrumb */}
            <div className="bd-header">
              <div>
                {drillCategory ? (
                  <div className="bd-breadcrumb">
                    <button className="bd-back-btn" onClick={() => setDrillCategory(null)}>
                      &#8592; All Categories
                    </button>
                    <span className="bd-breadcrumb-sep">›</span>
                    <span className="bd-breadcrumb-cur">{drillCategory.label}</span>
                  </div>
                ) : (
                  <h2 className="bd-title">&#127991; Bulk Category Discount</h2>
                )}
                <p className="bd-subtitle">
                  {drillCategory
                    ? `Set discount % for individual subcategories within ${drillCategory.label}`
                    : "Set a discount % for all products in a category with one click"}
                </p>
              </div>
            </div>

            {/* Global strip — only on top level */}
            {!drillCategory && (
              <div className="bd-global-strip">
                <span className="bd-global-label">Apply same discount to ALL categories:</span>
                <div className="bd-global-input-wrap">
                  <input type="number" min="0" max="100" className="bd-pct-input"
                    placeholder="e.g. 10" value={globalDiscount}
                    onChange={(e) => setGlobalDiscount(e.target.value)} />
                  <span className="bd-pct-sign">%</span>
                </div>
                <button className="bd-apply-all-btn" disabled={!globalDiscount || globalSaving}
                  onClick={() => setConfirmDiscount({
                    key: "__all__", label: "All Categories",
                    count: activeProducts.length, pct: globalDiscount,
                    filterFn: (prods) => prods,
                  })}>
                  {globalSaving ? "Applying..." : `Apply to All (${activeProducts.length} products)`}
                </button>
              </div>
            )}

            {/* Cards */}
            <div className="bd-cards-grid">
              {(drillCategory ? discountSubcategoryData : discountCategoryData).map((item) => {
                const key    = `${drillCategory?.value ?? "cat"}__${item.value}`;
                const input  = discountInputs[key] ?? "";
                const saving = discountSaving[key];
                const result = discountResult[key];

                const filterFn = drillCategory
                  ? (prods) => prods.filter(
                      (p) => p.category?.toLowerCase()    === drillCategory.value?.toLowerCase()
                          && p.subcategory?.toLowerCase() === item.value?.toLowerCase()
                    )
                  : (prods) => prods.filter(
                      (p) => p.category?.toLowerCase() === item.value?.toLowerCase()
                    );

                const hasSubs = !drillCategory
                  && menuItems.find((m) => m.value === item.value)?.dropdown?.length > 0;

                const level = item.avgDisc === 0 ? "none"
                  : item.avgDisc < 10 ? "low"
                  : item.avgDisc < 20 ? "mid"
                  : "high";

                return (
                  <div key={key} className={`bd-card bd-card-${level} ${result?.ok ? "bd-card-done" : ""}`}>
                    <div className="bd-card-top">
                      <span className="bd-card-name">{item.label}</span>
                      <span className="bd-card-count">{item.count} products</span>
                    </div>

                    <div className="bd-card-stats">
                      <div className="bd-stat">
                        <span className="bd-stat-label">Avg Discount</span>
                        <span className={`bd-stat-value bd-disc-val-${level}`}>
                          {item.avgDisc > 0 ? `${item.avgDisc}%` : "—"}
                        </span>
                      </div>
                      <div className="bd-stat">
                        <span className="bd-stat-label">Range</span>
                        <span className="bd-stat-value" style={{ fontSize: 13 }}>
                          {item.avgDisc > 0 ? `${item.minDisc}% – ${item.maxDisc}%` : "—"}
                        </span>
                      </div>
                    </div>

                    <div className="bd-card-input-row">
                      <div className="bd-input-wrap">
                        <input type="number" min="0" max="100" className="bd-pct-input"
                          placeholder="New %" value={input}
                          onChange={(e) => setDiscountInputs((prev) => ({ ...prev, [key]: e.target.value }))} />
                        <span className="bd-pct-sign">%</span>
                      </div>
                      <button className="bd-apply-btn" disabled={!input || saving}
                        onClick={() => setConfirmDiscount({ key, label: item.label, count: item.count, pct: input, filterFn })}>
                        {saving ? "..." : "Apply"}
                      </button>
                    </div>

                    {hasSubs && (
                      <button className="bd-subcat-btn"
                        onClick={() => setDrillCategory(menuItems.find((m) => m.value === item.value))}>
                        View Subcategories ›
                      </button>
                    )}

                    {result && (
                      <div className={`bd-result ${result.err ? "bd-result-err" : "bd-result-ok"}`}>
                        {result.ok > 0 && `✓ ${result.ok} updated`}
                        {result.err > 0 && ` ✗ ${result.err} failed`}
                      </div>
                    )}
                  </div>
                );
              })}

              {(drillCategory ? discountSubcategoryData : discountCategoryData).length === 0 && (
                <div className="bd-empty">No products found in this section.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Discount Modal */}
      {confirmDiscount && (
        <div className="bd-confirm-overlay" onClick={() => setConfirmDiscount(null)}>
          <div className="bd-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="bd-confirm-icon">&#127991;</div>
            <h3 className="bd-confirm-title">Confirm Bulk Discount</h3>
            <p className="bd-confirm-msg">
              Set <strong>{confirmDiscount.pct}%</strong> discount on{" "}
              <strong>{confirmDiscount.count} products</strong> in{" "}
              <strong>{confirmDiscount.label}</strong>?
            </p>
            <div className="bd-confirm-actions">
              <button className="bd-cancel-btn" onClick={() => setConfirmDiscount(null)}>Cancel</button>
              <button
                className="bd-confirm-btn"
                onClick={() => {
                  if (confirmDiscount.key === "__all__") setGlobalSaving(true);
                  applyDiscount(confirmDiscount.filterFn, confirmDiscount.label, confirmDiscount.pct, confirmDiscount.key);
                }}
              >
                Yes, Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
{selectedProduct && <ProductPopup product={selectedProduct} onClose={() => setSelectedProduct(null)} />}
      {barcodeProduct && <BarcodeModal product={barcodeProduct} onClose={() => setBarcodeProduct(null)} />}

      {archivePopup && (() => {
        const { product, archived } = archivePopup;
        const item = { image: product.images?.[0], name: product.name, sub: product.unique_id };
        return archived ? (
          <ActionPopup
            title="Archive this product?" icon="🗄️" tone="danger" confirmText="Yes, archive"
            message="It will be hidden from the store. You can restore it anytime from the Archived tab."
            item={item}
            onConfirm={() => archiveProduct(product.id, true)}
            workingText="Archiving…" doneTitle="Archived" doneText="The product is hidden from the store and moved to the Archived tab."
            onClose={() => setArchivePopup(null)} />
        ) : (
          <ActionPopup
            title="Restore this product?" icon="↩️" tone="success" confirmText="Yes, restore"
            message="It will show in the store again."
            item={item}
            onConfirm={() => archiveProduct(product.id, false)}
            workingText="Restoring…" doneTitle="Restored" doneText="The product is back in the store."
            onClose={() => setArchivePopup(null)} />
        );
      })()}

      <ToastContainer />
    </>
  );
};

export default ListProduct;
